import { memo, useCallback, useMemo, useState } from 'react';
import {
  ActivateCard,
  Button,
  Card,
  CreditCard,
  DepositCard,
  Error,
  LoadingStatus,
  Modal,
  NoFound,
  Select,
  WarningStatus,
} from '@/components/generic';
import { SelectOption } from '@/types/select';
import { formatCardNumber, formatSensitiveCardNumber } from '@/utils';
import {
  useGetInfoCard,
  useGetOnchainUserTxs,
} from '@package/smart-wallet/hooks';

import { useCardContext } from '../context/useCardContext';
import UpdateCardAlias from './UpdateCardAlias';

const CardDetails = () => {
  const [isModalActivateCardOpen, setisModalActivateCardOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalViewSensitiveData, setIsModalViewSensitiveData] =
    useState<boolean>(false);
  const { records: transactionsData } = useGetOnchainUserTxs({
    pageNum: 1,
  });

  const {
    selectedCard,
    selectedCardInfo,
    setSelectedCard,
    cardsData,
    cardOptions,
    isLoading: isCardLoading,
    error: errorCardsData,
    refetch: refetchCards,
  } = useCardContext();

  const selectedCardId = selectedCard?.id || 0;
  const {
    cardInfo,
    isLoading: isLoadingCardInfo,
    getSensitiveData,
    sensitiveData,
    error: errorCardInfo,
  } = useGetInfoCard({
    cardId: selectedCardId,
    enabled: !selectedCardInfo?.alias || isModalOpen,
  });

  const cardSelected = useMemo(() => {
    return selectedCardInfo?.alias ? selectedCardInfo : cardInfo;
  }, [cardInfo, selectedCardInfo]);

  // Helper function: Check if card is Apple Pay type
  const isCardApplePay = useMemo(() => {
    const cardTypeId = cardSelected?.cardTypeId || 0;
    return cardTypeId === 111028;
  }, [cardSelected]);

  // Helper function: Check if deposits are restricted
  const isDepositRestricted = useMemo(() => {
    const cardTypeId = cardSelected?.cardTypeId || 0;
    return cardTypeId === 111033;
  }, [cardSelected]);

  // Helper function: Check if card activation is processing
  const hasProcessingCardActivation = useMemo(() => {
    if (
      !transactionsData ||
      transactionsData.length === 0 ||
      !cardSelected?.cardNumber
    )
      return false;
    return transactionsData.some(
      (transaction: any) =>
        transaction?.type === 'CARD_ACTIVE' &&
        (transaction.status === 'PENDING' ||
          transaction.status === 'PROCESSING') &&
        transaction?.description &&
        transaction?.description.includes(cardSelected.cardNumber),
    );
  }, [transactionsData, cardSelected?.cardNumber]);

  // Helper function: Check if card is already activated
  const isCardAlreadyActivated = useMemo(() => {
    if (
      !transactionsData ||
      transactionsData.length === 0 ||
      !cardSelected?.cardNumber
    )
      return false;
    return transactionsData.some(
      (transaction: any) =>
        transaction.type === 'CARD_ACTIVE' &&
        transaction.status === 'SUCCESS' &&
        transaction?.description &&
        transaction?.description.includes(cardSelected.cardNumber),
    );
  }, [transactionsData, cardSelected?.cardNumber]);

  // Helper function: Check if card is canceled
  const isCardCanceled = useMemo(
    () => cardSelected?.status === 'cancel',
    [cardSelected],
  );

  const isCardUnActivated = useMemo(
    () => cardSelected?.status === 'un_activated',
    [cardSelected],
  );

  const isLoading = useMemo(
    () => isCardLoading || isLoadingCardInfo,
    [isCardLoading, isLoadingCardInfo],
  );

  // Handler: View card sensitive data
  const handleViewCard = useCallback(() => {
    setIsModalViewSensitiveData(false);
    getSensitiveData();
  }, [getSensitiveData]);

  // Handler: Change selected card
  const handleCardChange = useCallback(
    (option: SelectOption) => {
      setSelectedCard({
        id: option.id,
        name: option.name,
        value: option.value,
      });
    },
    [setSelectedCard],
  );

  // Handler: Alias updated successfully
  const handleAliasUpdate = useCallback(() => {
    refetchCards();
  }, [refetchCards]);

  return (
    <>
      <Card
        classHead='!px-1 max-sm:flex-col max-sm:items-start max-sm:mb-2'
        title='Card Details'
        className=' transition-shadow hover:shadow-depth overflow-hidden '
        headContent={
          <div className='flex gap-2  max-sm:w-full'>
            {!hasProcessingCardActivation && isCardApplePay && (
              <Button
                className='!px-4 !py-2 !h-8 max-sm:flex-1'
                isGray
                type='button'
                onClick={() => setisModalActivateCardOpen(() => true)}
                disabled={
                  !selectedCardId ||
                  !cardSelected ||
                  isLoading ||
                  hasProcessingCardActivation ||
                  isCardAlreadyActivated
                }
              >
                {isCardAlreadyActivated ? 'Already Verified' : 'Verify Card'}
              </Button>
            )}
            {cardSelected && (
              <UpdateCardAlias
                cardId={selectedCardId}
                currentAlias={cardSelected.alias || ''}
                onSuccess={handleAliasUpdate}
              />
            )}
            {!isCardUnActivated && <Button
              className='!px-4 !py-2 !h-8 max-sm:flex-1'
              isGray
              type='button'
              disabled={
                !selectedCardId || !cardSelected || isLoading || isCardCanceled
              }
              onClick={() => setIsModalViewSensitiveData(true)}
            >
              View Card
            </Button>}
            {!(isCardCanceled || isDepositRestricted || isCardUnActivated) && (
              <Button
                className='!px-4 !py-2 !h-8 max-sm:flex-1'
                isBlack
                type='button'
                disabled={
                  !selectedCardId ||
                  !cardSelected ||
                  isLoading ||
                  isCardCanceled ||
                  isDepositRestricted ||
                  isCardUnActivated
                }
                onClick={() => setIsModalOpen(true)}
              >
                Deposit
              </Button>
            )}
          </div>
        }
      >
        {hasProcessingCardActivation && (
          <div
            className={`label capitalize label-yellow !p-2 mb-2 w-full h-min`}
          >
            This card ({formatCardNumber(cardSelected?.cardNumber)}) activation
            is being processed. You cannot activate it again during this time.
          </div>
        )}
        {isCardAlreadyActivated && cardSelected?.cardNumber && (
          <div className='mb-4 p-3 bg-green-50 border border-green-200 rounded-lg'>
            <div className='flex items-center gap-2'>
              <div className='w-2 h-2 bg-green-500 rounded-full'></div>
              <span className='text-sm text-green-800 font-medium'>
                Card already activated
              </span>
            </div>
            <p className='text-xs text-green-700 mt-1'>
              This card ({formatCardNumber(cardSelected.cardNumber)}) has
              already been successfully activated and is ready to use.
            </p>
          </div>
        )}
        {isDepositRestricted && (
          <div className='mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg'>
            <div className='flex items-center gap-2'>
              <svg
                className='w-5 h-5 text-orange-500 shrink-0'
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                  clipRule='evenodd'
                />
              </svg>
              <span className='text-sm text-orange-800 font-medium'>
                Deposits Restricted
              </span>
            </div>
            <p className='text-xs text-orange-700 mt-1'>
              Deposits are not available for this card type. Please use a
              different card or contact support for more information.
            </p>
          </div>
        )}

        {cardsData && cardsData.length > 0 ? (
          <>
            {/* Card Selector */}
            {cardsData.length > 1 && (
              <div className='mb-6'>
                <Select
                  label='Select Card'
                  value={selectedCard}
                  onChange={handleCardChange}
                  options={cardOptions}
                  placeholder='Choose a card'
                />
              </div>
            )}

            {/* Card Display */}
            {!cardSelected && isLoading && (
              <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none'>
                <LoadingStatus onlySpinner />
              </div>
            )}
            {cardSelected && (
              <div className='mb-4 relative'>
                <CreditCard
                  cardInfo={{
                    cardNumber:
                      sensitiveData?.cardNumber ??
                      formatSensitiveCardNumber(cardSelected.cardNumber),
                    tier:
                      typeof cardSelected.tier === 'object'
                        ? cardSelected.tier?.name || ''
                        : cardSelected.tier,
                  }}
                  sensitiveData={sensitiveData || undefined}
                  isCanceled={isCardCanceled}
                  disabled={isLoading}
                />
                {isLoading && (
                  <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none'>
                    <LoadingStatus onlySpinner />
                  </div>
                )}
              </div>
            )}

            <Error
              error={errorCardsData || errorCardInfo}
              message={
                errorCardsData?.message ||
                errorCardInfo?.message ||
                'An error occurred while fetching card details.'
              }
            />
          </>
        ) : (
          <NoFound
            title='No cards found'
            sub='Try getting a new card'
            options={['Get New Card']}
          />
        )}
      </Card>
      <Modal
        open={isModalViewSensitiveData}
        onClose={() => setIsModalViewSensitiveData(false)}
      >
        <WarningStatus
          onClose={() => setIsModalViewSensitiveData(false)}
          confirm={() => handleViewCard()}
          content={
            <>
              <span className='text-t-secondary text-body-2 font-light'>
                You're about to view sensitive information . This includes the
                <strong className='font-extrabold'>
                  {' '}
                  full card number, expiry date, and CVV.{' '}
                </strong>
                Please ensure you're in a secure environment before proceeding.
              </span>
            </>
          }
        />
      </Modal>
      {cardSelected && (
        <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <DepositCard
            onClose={() => setIsModalOpen(false)}
            cardInfo={{
              ...cardSelected,
              holderId: cardInfo?.holderId || cardSelected?.holderId,
              balanceInfo: (cardInfo?.balanceInfo ||
                cardSelected?.balanceInfo)!,
            }}
          />
        </Modal>
      )}
      <Modal
        open={isModalActivateCardOpen}
        onClose={() => setisModalActivateCardOpen(false)}
      >
        <ActivateCard
          cardInfo={cardSelected}
          onClose={() => setisModalActivateCardOpen(false)}
        />
      </Modal>
    </>
  );
};

export default memo(CardDetails);
